<template>
	<div class="custom-slider-wrapper">
		<div ref="line" class="custom-slider-wrapper__background-line"></div>
		<input
			ref="slider"
			:name="name"
			:value="value"
			:style="
				blockSlider && {
					width:
						initialValue > (recommendedMin ? recommendedMin : 0)
							? '100%'
							: '25%'
				}
			"
			class="slider"
			type="range"
			:max="showMinDot ? recommendedMax + recommendedMax * 0.1 : max"
			:min="min"
			:step="step"
			tabindex="-1"
			aria-hidden="true"
			@input="onChange($event.target.value)"
		/>
		<button
			v-if="recommendedMin"
			:disabled="initialValue < recommendedMin"
			class="reccomendation-tick"
			:style="{ left: recommendedMinPosition + 'px' }"
			:aria-label="
				$t('setRecommendedMinBudget', { name: name, amount: recommendedMin })
			"
			@click="onChange(recommendedMin)"
			@mousedown.prevent
		></button>
		<button
			v-if="recommendedMin"
			:disabled="initialValue < recommendedMin"
			class="reccomendation-tick max-dot"
			:style="{ left: recommendedMaxPosition + 'px' }"
			:aria-label="
				$t('setRecommendedMaxBudget', { name: name, amount: recommendedMax })
			"
			@click="onChange(recommendedMax)"
			@mousedown.prevent
		></button>
		<div
			class="range-line"
			:style="{
				left: recommendedMinPosition + 5 + 'px',
				width: width,
				visibility: showRange ? 'visible' : 'hidden'
			}"
		></div>
	</div>
</template>
<script>
export default {
	props: {
		showTooltips: {
			type: Boolean,
			default: false
		},
		max: {
			type: Number,
			default: 10
		},
		min: {
			type: Number,
			default: 1
		},
		recommendedMax: {
			type: Number,
			default: undefined
		},
		recommendedMin: {
			type: Number,
			default: undefined
		},
		step: {
			type: Number,
			default: 1
		},
		budget: {
			type: Number,
			default: 0
		},
		value: {
			type: Number,
			default: 0
		},
		changeHandler: {
			type: Function,
			default: () => {}
		},
		disabled: {
			type: Boolean,
			default: false
		},
		name: {
			type: String,
			default: ''
		},
		blockSlider: {
			type: Boolean,
			default: false
		},
		limitValue: {
			type: Number,
			default: 50
		},
		isLimitValue: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			recommendedMinPosition: null,
			recommendedMaxPosition: null,
			tickStepPx: null,
			recommendationTickWidth: 15, //px
			sliderThumbWidth: 10, //px
			showRange: false,
			initialValue: 0,
			externalBudgetChanged: false,
			currentValue: this.value
		};
	},
	computed: {
		showMinDot() {
			return !this.disabled && this.max >= this.recommendedMin;
		},
		width() {
			const range = this.recommendedMax - this.recommendedMin;
			const thumbPos = (this.value - this.recommendedMin) / range;
			const width =
				thumbPos * (this.recommendedMaxPosition - this.recommendedMinPosition);
			return width + 'px';
		}
	},
	watch: {
		max() {
			this.calculateSliderElements();
		},
		value(newValue) {
			this.currentValue = newValue;
			this.showRange =
				this.value >= this.recommendedMin && this.value <= this.recommendedMax;
		},
		budget(newValue) {
			this.initialValue = newValue;
		}
	},
	mounted() {
		this.calculateSliderElements();
		window.addEventListener('resize', this.calculateSliderElements);
		this.showRange =
			this.value >= this.recommendedMin && this.value <= this.recommendedMax;
		this.initialValue = this.budget;
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.calculateSliderElements);
	},
	methods: {
		onChange(value) {
			const numericValue = parseInt(value);
			if (numericValue < this.limitValue) {
				this.currentValue = numericValue;
			}
			if (numericValue > this.limitValue && this.isLimitValue) {
				this.currentValue = this.limitValue;
			} else {
				this.currentValue = numericValue;
			}
			this.changeHandler(this.currentValue);
			this.$emit('input', this.currentValue);
		},
		calculateSliderElements() {
			if (this.recommendedMin >= 0) {
				this.recommendedMinPosition = this.calculateMinTickPosition(
					this.recommendedMin
				);
			}
			if (this.recommendedMax >= 0) {
				this.recommendedMaxPosition = this.calculateMaxTickPosition(
					this.recommendedMax
				);
			}
		},
		calculateMinTickPosition() {
			const sumTenPercentRecommendedMax =
				(10 / 100) * this.recommendedMax + this.recommendedMax;
			const tickPosition =
				((this.recommendedMin - this.$refs.slider.min) /
					(sumTenPercentRecommendedMax - this.$refs.slider.min)) *
					this.$refs.line.clientWidth -
				5;
			return tickPosition;
		},
		calculateMaxTickPosition() {
			const sumTenPercentRecommendedMax =
				(10 / 100) * this.recommendedMax + this.recommendedMax;
			const tickPosition =
				((this.recommendedMax - this.$refs.slider.min) /
					(sumTenPercentRecommendedMax - this.$refs.slider.min)) *
					this.$refs.line.clientWidth -
				this.recommendationTickWidth;

			return tickPosition;
		}
	}
};
</script>
<style lang="scss" scoped>
$height: 60px;
$thumb-height: 28px;
$thumb-width: 10px;
$track-height: 13px;

$lower-background: linear-gradient(to bottom, $disabled-state, $disabled-state)
	100% 50% / 100% $track-height no-repeat transparent;
$upper-background: linear-gradient(to bottom, $primary-state, $primary-state)
	100% 50% / 100% $track-height no-repeat transparent;

.custom-slider-wrapper {
	position: relative;

	&__background-line {
		width: 100%;
		height: $height;
		background: $lower-background;
		margin-top: -15px;
		position: absolute;
		z-index: 0;
		pointer-events: none;
	}

	.reccomendation-tick {
		position: absolute;
		height: 15px;
		width: 15px;
		border-radius: 100%;
		background-color: $budget-min-color;
		border: 1px solid $page-bgcolor;
		top: 7px;
		padding: 0;
		z-index: 3;

		&:hover {
			cursor: pointer;
		}
		&:disabled {
			cursor: default;
		}
	}
	.range-line {
		position: absolute;
		top: 15px;
		transform: translateY(-50%);
		height: 13px;
		z-index: 1;
		background-color: $icon-completed;
		transition: width 0.1s ease-in-out;
		pointer-events: none;
	}

	.slider {
		display: block;
		appearance: none;
		-webkit-appearance: none;
		margin: 0;
		height: $height;
		overflow: hidden;
		background-color: transparent;
		cursor: pointer;
		margin-top: -15px;
		padding-left: 5px;
		padding-right: 5px;
		width: 100%;

		&:focus {
			outline: none;
		}

		&::-webkit-slider-runnable-track {
			width: 100%;
			height: $height;
			background: $lower-background;
		}

		&::-webkit-slider-thumb {
			position: relative;
			appearance: none;
			-webkit-appearance: none;
			height: $thumb-height;
			width: $thumb-width;
			border: 0;
			outline: none;
			margin-top: 15px;
			transition: background-color 150ms;
			transform: scale(2);
			background-color: transparent;
			background-size: cover;
			background-position: center;
			background-image: url('../../../assets/images/slider-icon.png');
			z-index: 3;
		}

		&::-moz-focus-outer {
			border: 0;
		}

		&::-moz-range-track,
		&::-moz-range-progress {
			width: 100%;
			height: $height;
			background: $lower-background;
		}

		&::-moz-range-thumb {
			appearance: none;
			height: $thumb-height;
			width: $thumb-width;
			border: 0;
			background-color: transparent;
			transition: background-color 150ms;
			transform: scale(2);
			background-color: transparent;
			background-size: cover;
			background-position: center;
			background-image: url('../../../assets/images/slider-icon.png');
			z-index: 2;
		}
	}
}
</style>
